// --- types ---
import type { Sheet } from '../types/types/sheet.js';
import type { Field, FieldType, FieldConfig } from '../types/types/field.js';
import type { Row, RowData } from '../types/types/row.js';
import type { Attachment } from '../views/grid/types/gridTypes.js';

// =============================================================================
// API BASE CONFIGURATION
// =============================================================================

const API_BASE = import.meta.env.VITE_API_URL;

// =============================================================================
// HELPERS
// =============================================================================

type ApiFieldType = Uppercase<FieldType>;

/**
 * Convert a frontend field type (lowercase) to the API/Prisma enum (uppercase)
 */
function toApiFieldType(type?: FieldType): ApiFieldType | undefined {
  return type ? (type.toUpperCase() as ApiFieldType) : undefined;
}

/**
 * Normalize a field returned by the API to use the frontend's lowercase type
 */
function normalizeFieldFromApi(field: Record<string, unknown> & { type: string }): Field {
  return { ...field, type: field.type.toLowerCase() as FieldType } as unknown as Field;
}

function normalizeFieldsFromApi(fields: Array<Record<string, unknown> & { type: string }>): Field[] {
  return fields.map(normalizeFieldFromApi);
}

/**
 * Generic API error with status code and error details
 */
export class ApiError extends Error {
  constructor(
    public status: number,
    public code: string,
    message: string
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/**
 * Make an API request with proper error handling
 */
async function apiRequest<T>(
  url: string,
  options: RequestInit = {}
): Promise<T> {
  // Inject mock user header if available in local storage (for dev/mock mode)
  const storedUser = localStorage.getItem('auth_user');
  const authHeaders: Record<string, string> = {};
  
  if (storedUser) {
    try {
      const user = JSON.parse(storedUser);
      
      // Story 4.6: Get current workspace context (domain)
      const currentDomainId = localStorage.getItem('swm-current-workspace-id');

      // Format must match keys expected by authMiddleware.ts
      authHeaders['X-Mock-User'] = JSON.stringify({
        id: user.id,
        email: user.email,
        tenantId: user.tenantId, 
        domainId: currentDomainId || undefined, // Add domain context
        role: user.role || 'member'
      });
    } catch {
      // Ignore parse errors
    }
  }

  const response = await fetch(`${API_BASE}${url}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...authHeaders,
      ...options.headers,
    },
  });

  if (!response.ok) {
    const errorBody = await response.json().catch(() => ({}));
    throw new ApiError(
      response.status,
      errorBody.code || 'UNKNOWN_ERROR',
      errorBody.message || `Request failed with status ${response.status}`
    );
  }

  // Handle 204 No Content
  if (response.status === 204) {
    return undefined as T;
  }

  return response.json();
}

// =============================================================================
// RESPONSE TYPES
// =============================================================================

export interface FetchRowsResponse {
  rows: Row[];
  total: number;
}

export interface FetchFieldsResponse {
  fields: Field[];
}

export interface SheetWithRelations extends Sheet {
  fields: Field[];
  rows: Row[];
}

export interface CreateRowRequest {
  data: RowData;
}

export interface UpdateRowRequest {
  data: Partial<RowData>;
}

export interface CreateFieldRequest {
  name: string;
  type: FieldType;
  config?: FieldConfig;
  width?: number;
}

export interface UpdateFieldRequest {
  name?: string;
  type?: FieldType;
  config?: FieldConfig;
  width?: number;
}

// =============================================================================
// SHEET API
// =============================================================================

/**
 * Fetch a sheet by ID with fields and rows
 * GET /api/sheets/:sheetId
 */
export async function fetchSheet(sheetId: string): Promise<SheetWithRelations> {
  type ApiSheetWithRelations = Omit<SheetWithRelations, 'fields'> & {
    fields: Array<{ type: ApiFieldType } & Record<string, unknown>>;
  };

  const response = await apiRequest<ApiSheetWithRelations>(`/api/sheets/${sheetId}`);
  return {
    ...response,
    fields: normalizeFieldsFromApi(response.fields),
  };
}

// =============================================================================
// ROW API
// =============================================================================

/**
 * Fetch rows for a sheet with pagination
 * GET /api/sheets/:sheetId/rows
 */
export async function fetchRows(
  sheetId: string,
  options?: { limit?: number; offset?: number }
): Promise<FetchRowsResponse> {
  const params = new URLSearchParams();
  if (options?.limit) params.set('limit', String(options.limit));
  if (options?.offset) params.set('offset', String(options.offset));

  const query = params.toString();
  const url = `/api/sheets/${sheetId}/rows${query ? `?${query}` : ''}`;

  return apiRequest<FetchRowsResponse>(url);
}

/**
 * Create a new row in a sheet
 * POST /api/sheets/:sheetId/rows
 * AC-3.21: Creates row via POST API
 */
export async function createRow(
  sheetId: string,
  data: RowData = {}
): Promise<Row> {
  return apiRequest<Row>(`/api/sheets/${sheetId}/rows`, {
    method: 'POST',
    body: JSON.stringify({ data }),
  });
}

/**
 * Update a row's data (partial merge)
 * PATCH /api/rows/:rowId
 * AC-3.22: Updates row via PATCH API
 */
export async function updateRow(
  rowId: string,
  data: Partial<RowData>
): Promise<Row> {
  return apiRequest<Row>(`/api/rows/${rowId}`, {
    method: 'PATCH',
    body: JSON.stringify({ data }),
  });
}

/**
 * Soft delete a row
 * DELETE /api/rows/:rowId
 * AC-3.23: Soft-deletes row via DELETE API
 */
export async function deleteRow(rowId: string): Promise<{ success: true }> {
  return apiRequest<{ success: true }>(`/api/rows/${rowId}`, {
    method: 'DELETE',
  });
}

// =============================================================================
// FIELD API
// =============================================================================

/**
 * Fetch fields for a sheet
 * GET /api/sheets/:sheetId/fields
 */
export async function fetchFields(sheetId: string): Promise<FetchFieldsResponse> {
  const response = await apiRequest<{
    fields: Array<{ type: ApiFieldType } & Record<string, unknown>>;
  }>(`/api/sheets/${sheetId}/fields`);

  return {
    fields: normalizeFieldsFromApi(response.fields),
  };
}

/**
 * Create a new field in a sheet
 * POST /api/sheets/:sheetId/fields
 * AC-3.24: Creates field via POST API
 */
export async function createField(
  sheetId: string,
  field: CreateFieldRequest
): Promise<Field> {
  const payload = { ...field, type: toApiFieldType(field.type) };

  return apiRequest<{ type: ApiFieldType } & Record<string, unknown>>(
    `/api/sheets/${sheetId}/fields`,
    {
      method: 'POST',
      body: JSON.stringify(payload),
    }
  ).then(normalizeFieldFromApi);
}

/**
 * Update a field's properties
 * PATCH /api/fields/:fieldId
 * AC-3.25: Updates field via PATCH API
 */
export async function updateField(
  fieldId: string,
  updates: UpdateFieldRequest
): Promise<Field> {
  const payload = {
    ...updates,
    ...(updates.type ? { type: toApiFieldType(updates.type) } : {}),
  };

  return apiRequest<{ type: ApiFieldType } & Record<string, unknown>>(`/api/fields/${fieldId}`, {
    method: 'PATCH',
    body: JSON.stringify(payload),
  }).then(normalizeFieldFromApi);
}

/**
 * Soft delete a field
 * DELETE /api/fields/:fieldId
 * AC-3.26: Deletes field via DELETE API; requires confirm if field has data
 *
 * @param fieldId - Field ID to delete
 * @param confirm - If true, deletes even if field has data. If false and field has data, returns 400.
 */
export async function deleteField(
  fieldId: string,
  confirm: boolean = false
): Promise<{ success: true }> {
  const url = confirm ? `/api/fields/${fieldId}?confirm=true` : `/api/fields/${fieldId}`;
  return apiRequest<{ success: true }>(url, {
    method: 'DELETE',
  });
}

/**
 * Reorder fields within a sheet
 * POST /api/sheets/:sheetId/fields/reorder
 *
 * @param sheetId - Sheet ID
 * @param fieldIds - Array of field IDs in the new order
 */
export async function reorderFields(
  sheetId: string,
  fieldIds: string[]
): Promise<FetchFieldsResponse> {
  const response = await apiRequest<{
    fields: Array<{ type: ApiFieldType } & Record<string, unknown>>;
  }>(`/api/sheets/${sheetId}/fields/reorder`, {
    method: 'POST',
    body: JSON.stringify({ fieldIds }),
  });

  return {
    fields: normalizeFieldsFromApi(response.fields),
  };
}

// =============================================================================
// ATTACHMENT API FUNCTIONS
// =============================================================================

/**
 * Upload an attachment to a row
 */
export async function uploadAttachment(rowId: string, file: File): Promise<Attachment> {
  const formData = new FormData();
  formData.append('file', file);

  const response = await fetch(`${API_BASE}/api/rows/${rowId}/attachments`, {
    method: 'POST',
    body: formData,
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Upload failed' }));
    throw new ApiError(response.status, 'UPLOAD_FAILED', error.error || 'Failed to upload attachment');
  }

  return response.json();
}

/**
 * Get all attachments for a row
 */
export async function getAttachments(rowId: string): Promise<Attachment[]> {
  const response = await fetch(`${API_BASE}/api/rows/${rowId}/attachments`);

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Fetch failed' }));
    throw new ApiError(response.status, 'FETCH_FAILED', error.error || 'Failed to fetch attachments');
  }

  return response.json();
}

/**
 * Delete an attachment
 */
export async function deleteAttachment(rowId: string, attachmentId: string): Promise<void> {
  const response = await fetch(`${API_BASE}/api/rows/${rowId}/attachments/${attachmentId}`, {
    method: 'DELETE',
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Delete failed' }));
    throw new ApiError(response.status, 'DELETE_FAILED', error.error || 'Failed to delete attachment');
  }
}
